import { z } from 'zod';
import { requiredUuidString } from '../zod-helpers/index.js';
import { BOM_INPUT_REQUIRED_MESSAGE } from './bom-input-required-message.js';
import { BOM_ITEM_TYPES_CONSUMING_AS_INGREDIENT, BOM_ITEM_TYPES_LINKING_DEFAULT_BOM, } from './bom-item-types-requiring-part/index.js';
import { BOM_OUTPUT_REQUIRED_MESSAGE } from './bom-output-required-message.js';
import { bomWarehouseIdsSchema } from './bom-warehouse-ids-schema.js';
import { BOM_WAREHOUSES_REQUIRED_MESSAGE } from './bom-warehouses-required-message.js';
import { BOM_CONSTRAINTS, MAX_BOM_DEFAULT_FOR_PARTS, MAX_BOM_DEFAULT_FOR_PARTS_MESSAGE, MAX_BOM_ITEM_GROUPS, MAX_BOM_ITEM_GROUPS_MESSAGE, MAX_BOM_ITEMS, MAX_BOM_ITEMS_MESSAGE, } from './constraints.js';
import { createDefaultPartInputSchema } from './create-default-part-input.js';
import { bomDescriptionSchema, bomNoteSchema, bomNumSchema, bomUrlSchema } from './create-form.js';
import { createBomItemGroupInputSchema } from './create-item-group-input.js';
import { createBomItemInputSchema } from './create-item-input.js';
export const createBomInputSchema = z
    .object({
    num: bomNumSchema,
    description: bomDescriptionSchema,
    brandId: requiredUuidString(`${BOM_CONSTRAINTS.brand.label} is required.`, 'Invalid brand selection.'),
    pickFromLocation: z.boolean().optional(),
    url: bomUrlSchema,
    note: bomNoteSchema,
    // .nullish() so an explicit null from a direct API caller hits the same floor-rule message below as an omitted field, not a raw Zod array-type error.
    warehouseLocationGroupIds: bomWarehouseIdsSchema.nullish(),
    items: z.array(createBomItemInputSchema).max(MAX_BOM_ITEMS, MAX_BOM_ITEMS_MESSAGE).nullish(),
    itemGroups: z
        .array(createBomItemGroupInputSchema)
        .max(MAX_BOM_ITEM_GROUPS, MAX_BOM_ITEM_GROUPS_MESSAGE)
        .nullish(),
    defaultForParts: z
        .array(createDefaultPartInputSchema)
        .max(MAX_BOM_DEFAULT_FOR_PARTS, MAX_BOM_DEFAULT_FOR_PARTS_MESSAGE)
        .nullish(),
})
    .superRefine((values, context) => {
    const items = values.items ?? [];
    const itemGroups = values.itemGroups ?? [];
    const defaultForParts = values.defaultForParts ?? [];
    // A bare defaultForParts pointer (no BomItem row) is itself a supported Output — see setPartDefaultBom.
    const hasOutput = items.some((item) => BOM_ITEM_TYPES_LINKING_DEFAULT_BOM.has(item.typeId)) ||
        defaultForParts.length > 0;
    if (!hasOutput) {
        context.addIssue({ code: 'custom', message: BOM_OUTPUT_REQUIRED_MESSAGE, path: ['items'] });
    }
    // Option Group members are always RAW_GOOD (see item-group-member.ts), so a non-empty group
    // already satisfies this without needing its own typeId check.
    const hasInput = items.some((item) => BOM_ITEM_TYPES_CONSUMING_AS_INGREDIENT.includes(item.typeId)) ||
        itemGroups.some((group) => group.items.length > 0);
    if (!hasInput) {
        context.addIssue({ code: 'custom', message: BOM_INPUT_REQUIRED_MESSAGE, path: ['items'] });
    }
    if (!values.warehouseLocationGroupIds || values.warehouseLocationGroupIds.length === 0) {
        context.addIssue({
            code: 'custom',
            message: BOM_WAREHOUSES_REQUIRED_MESSAGE,
            path: ['warehouseLocationGroupIds'],
        });
    }
    // itemGroups[].items each pass their own MAX_BOM_ITEM_GROUP_MEMBERS cap, but nothing else
    // bounds their combined total against flat items -- matches the addBomItem-family ceiling on
    // the same BOM-wide bomItem count.
    const totalItemCount = items.length + itemGroups.reduce((total, group) => total + group.items.length, 0);
    if (totalItemCount > MAX_BOM_ITEMS) {
        context.addIssue({ code: 'custom', message: MAX_BOM_ITEMS_MESSAGE, path: ['items'] });
    }
    // addBomDefaultPart counts every Part.defaultBomId row against this same ceiling. items.max
    // and defaultForParts.max are independent, so a disjoint pair can overshoot unless combined here.
    const linkingDefaultCount = items.filter((item) => BOM_ITEM_TYPES_LINKING_DEFAULT_BOM.has(item.typeId) &&
        item.partId &&
        (item.linkAsDefault ?? true)).length;
    if (linkingDefaultCount + defaultForParts.length > MAX_BOM_DEFAULT_FOR_PARTS) {
        context.addIssue({
            code: 'custom',
            message: MAX_BOM_DEFAULT_FOR_PARTS_MESSAGE,
            path: ['defaultForParts'],
        });
    }
    const recipePartIds = new Set(items.map((item) => item.partId).filter((partId) => !!partId));
    const conflicting = defaultForParts.some((entry) => recipePartIds.has(entry.partId));
    if (conflicting) {
        context.addIssue({
            code: 'custom',
            message: 'A part already on this BOM’s recipe cannot also be a Default BOM For entry.',
            path: ['defaultForParts'],
        });
    }
    // No DB row to collide with yet at create time, so duplicate group names are caught here instead, normalized the same way assertBomItemGroupNameAvailable does (trim + lowercase).
    const seenGroupNames = new Set();
    itemGroups.forEach((group, index) => {
        const normalized = group.name.trim().toLowerCase();
        if (seenGroupNames.has(normalized)) {
            context.addIssue({
                code: 'custom',
                message: 'A group with this name already exists on this BOM.',
                path: ['itemGroups', index, 'name'],
            });
        }
        seenGroupNames.add(normalized);
    });
});
//# sourceMappingURL=create-input.js.map